import React, { useCallback, useState, useRef, useMemo } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { Block, ImageBlockData, ImageSize, ImageAlignment, ImageLayout, ImageAspect, ImageItem, generateBlockId } from '../../../config';
import { toBrowserImage } from '../../../utils/imageInput';
import { compressImage } from '../../../utils/images';
import LiveText from '../../LiveText';
import './image-block.scss';

interface ImageBlockProps {
  block: Block;
  onChange: (data: ImageBlockData) => void;
  onDelete: () => void;
  isEditing: boolean;
}

const SIZE_OPTIONS: { value: ImageSize; label: string }[] = [
  { value: 'small', label: 'S' },
  { value: 'medium', label: 'M' },
  { value: 'large', label: 'L' },
  { value: 'full', label: 'Full' },
];

const ASPECT_OPTIONS: { value: ImageAspect; label: string; description: string }[] = [
  { value: 'horizontal-4-3', label: 'Horizontal 4:3', description: 'Standard photo' },
  { value: 'horizontal-16-9', label: 'Horizontal 16:9', description: 'Widescreen' },
  { value: 'vertical-3-4', label: 'Vertical 3:4', description: 'Portrait photo' },
  { value: 'vertical-9-16', label: 'Vertical 9:16', description: 'Phone portrait' },
  { value: 'square', label: 'Square', description: '1:1' },
];

const LAYOUT_OPTIONS: { value: ImageLayout; label: string }[] = [
  { value: 'single', label: 'Single' },
  { value: 'two-column', label: '2 Column' },
  { value: 'grid', label: 'Grid' },
];

// Get CSS aspect-ratio value from aspect option
const getAspectRatio = (aspect: ImageAspect): string => {
  switch (aspect) {
    case 'horizontal-4-3': return '4/3';
    case 'horizontal-16-9': return '16/9';
    case 'vertical-3-4': return '3/4';
    case 'vertical-9-16': return '9/16';
    case 'square': return '1/1';
    default: return '4/3'; // Default to 4:3
  }
};

const ImageBlock: React.FC<ImageBlockProps> = ({ block, onChange, isEditing }) => {
  const supabase = useSupabaseClient();
  const data = block.data as ImageBlockData;
  const [uploading, setUploading] = useState(false);
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [showAspectMenu, setShowAspectMenu] = useState(false);
  const blockRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Defaults
  const size = data.size || 'medium';
  const alignment = data.alignment || 'center';
  const layout = data.layout || 'single';
  const aspect = data.aspect || 'horizontal-4-3';
  const images = useMemo(() => data.images || [], [data.images]);

  // Load signed URLs for all images
  React.useEffect(() => {
    const loadSignedUrls = async () => {
      const urlMap: Record<string, string> = {};
      
      if (data.storagePath) {
        const { data: urlData } = await supabase.storage
          .from('document-images')
          .createSignedUrl(data.storagePath, 3600);
        if (urlData?.signedUrl) {
          urlMap['main'] = urlData.signedUrl;
        }
      }

      for (const img of images) {
        const { data: urlData } = await supabase.storage
          .from('document-images')
          .createSignedUrl(img.storagePath, 3600);
        if (urlData?.signedUrl) {
          urlMap[img.id] = urlData.signedUrl;
        }
      }

      setImageUrls(urlMap);
    };

    loadSignedUrls();
  }, [data.storagePath, images, supabase]);

  const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      setError(null);

      // Allow larger files since we'll compress them (up to 20MB original)
      if (file.size > 20 * 1024 * 1024) {
        setError('Image must be less than 20MB');
        return;
      }

      // HEIC/HEIF (iPhone) photos are converted to JPEG first; other files pass through
      const sourceFile = await toBrowserImage(file);

      // Compress image before upload
      let uploadData: Blob | File = sourceFile;

      if (sourceFile.type.startsWith('image/')) {
        try {
          uploadData = await compressImage(sourceFile, {
            maxWidth: 1920,  // Max width for documents
            maxHeight: 1920, // Max height for documents
            quality: 0.85    // Good quality, ~85%
          });
        } catch (compressError) {
          console.warn('Image compression failed, uploading original:', compressError);
          // Fall back to original file if compression fails
        }
      }

      // Always save as JPEG after compression
      const fileName = `${crypto.randomUUID()}.jpeg`;
      const filePath = fileName;

      const { error: uploadError } = await supabase.storage
        .from('document-images')
        .upload(filePath, uploadData, {
          contentType: 'image/jpeg'
        });

      if (uploadError) {
        setError(uploadError.message || 'Failed to upload image');
        return;
      }

      const { data: urlData, error: urlError } = await supabase.storage
        .from('document-images')
        .createSignedUrl(filePath, 3600);

      if (urlError || !urlData?.signedUrl) {
        setError('Image uploaded but failed to generate preview URL');
        return;
      }

      if (layout === 'single' && !data.storagePath) {
        setImageUrls(prev => ({ ...prev, main: urlData.signedUrl }));
        onChange({
          ...data,
          imageId: fileName,
          storagePath: filePath
        });
      } else {
        const newImage: ImageItem = {
          id: generateBlockId(),
          storagePath: filePath,
          caption: '',
          alt: ''
        };
        setImageUrls(prev => ({ ...prev, [newImage.id]: urlData.signedUrl }));
        onChange({
          ...data,
          images: [...images, newImage]
        });
      }

    } catch (err) {
      console.error('Error uploading image:', err);
      setError('An unexpected error occurred while uploading');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [data, onChange, supabase, layout, images]);

  const handleCaptionChange = useCallback((caption: string) => {
    onChange({ ...data, caption });
  }, [data, onChange]);

  const handleRemoveMainImage = useCallback(() => {
    onChange({ ...data, imageId: null, storagePath: null });
    setImageUrls(prev => {
      const next = { ...prev };
      delete next['main'];
      return next;
    });
  }, [data, onChange]);

  const handleRemoveImage = useCallback((imageId: string) => {
    onChange({
      ...data,
      images: images.filter(img => img.id !== imageId)
    });
    setImageUrls(prev => {
      const next = { ...prev };
      delete next[imageId];
      return next;
    });
  }, [data, onChange, images]);

  const handleSizeChange = useCallback((newSize: ImageSize) => {
    onChange({ ...data, size: newSize });
  }, [data, onChange]);

  const handleAlignmentChange = useCallback((newAlignment: ImageAlignment) => {
    onChange({ ...data, alignment: newAlignment });
  }, [data, onChange]);

  const handleLayoutChange = useCallback((newLayout: ImageLayout) => {
    onChange({ ...data, layout: newLayout });
  }, [data, onChange]);

  const handleAspectChange = useCallback((newAspect: ImageAspect) => {
    onChange({ ...data, aspect: newAspect });
    setShowAspectMenu(false);
  }, [data, onChange]);

  const handleFocus = useCallback(() => setIsFocused(true), []);
  const handleBlur = useCallback((e: React.FocusEvent) => {
    if (!blockRef.current?.contains(e.relatedTarget as Node)) {
      setIsFocused(false);
      setShowAspectMenu(false);
    }
  }, []);

  const triggerFileUpload = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const hasMainImage = !!imageUrls['main'];
  const hasAnyImages = hasMainImage || images.length > 0;
  const showToolbar = isEditing && isFocused && hasAnyImages;

  // Get size percentage
  const sizePercent = {
    small: '33%',
    medium: '50%',
    large: '75%',
    full: '100%'
  }[size];

  const aspectRatio = getAspectRatio(aspect);
  const currentAspectOption = ASPECT_OPTIONS.find(opt => opt.value === aspect);

  return (
    <div 
      ref={blockRef}
      className={`image-block layout-${layout} align-${alignment}`}
      tabIndex={isEditing ? 0 : undefined}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileUpload}
        disabled={uploading}
        hidden
      />

      {/* Toolbar */}
      {showToolbar && (
        <div className="image-toolbar">
          {/* Aspect/Orientation dropdown */}
          <div className="toolbar-group aspect-group">
            <button 
              className="aspect-dropdown-btn"
              onClick={() => setShowAspectMenu(!showAspectMenu)}
            >
              <span className="aspect-label">{currentAspectOption?.label}</span>
              <span className="dropdown-arrow">▾</span>
            </button>
            
            {showAspectMenu && (
              <div className="aspect-menu">
                <div className="aspect-section">
                  <div className="section-label">Orientation</div>
                  {ASPECT_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      className={`aspect-option ${aspect === opt.value ? 'active' : ''}`}
                      onClick={() => handleAspectChange(opt.value)}
                    >
                      <span className="option-preview" data-aspect={opt.value} />
                      <span className="option-text">
                        <span className="option-label">{opt.label}</span>
                        <span className="option-desc">{opt.description}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="toolbar-divider" />

          {/* Size options */}
          <div className="toolbar-group">
            <span className="group-label">Size</span>
            <div className="toolbar-buttons">
              {SIZE_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  className={`toolbar-btn ${size === opt.value ? 'active' : ''}`}
                  onClick={() => handleSizeChange(opt.value)}
                  title={opt.label}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="toolbar-divider" />

          {/* Alignment options */}
          <div className="toolbar-group">
            <div className="toolbar-buttons">
              <button
                className={`toolbar-btn align-btn ${alignment === 'left' ? 'active' : ''}`}
                onClick={() => handleAlignmentChange('left')}
                title="Align left"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <rect x="1" y="2" width="14" height="2" rx="0.5" />
                  <rect x="1" y="7" width="10" height="2" rx="0.5" />
                  <rect x="1" y="12" width="12" height="2" rx="0.5" />
                </svg>
              </button>
              <button
                className={`toolbar-btn align-btn ${alignment === 'center' ? 'active' : ''}`}
                onClick={() => handleAlignmentChange('center')}
                title="Align center"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <rect x="1" y="2" width="14" height="2" rx="0.5" />
                  <rect x="3" y="7" width="10" height="2" rx="0.5" />
                  <rect x="2" y="12" width="12" height="2" rx="0.5" />
                </svg>
              </button>
              <button
                className={`toolbar-btn align-btn ${alignment === 'right' ? 'active' : ''}`}
                onClick={() => handleAlignmentChange('right')}
                title="Align right"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <rect x="1" y="2" width="14" height="2" rx="0.5" />
                  <rect x="5" y="7" width="10" height="2" rx="0.5" />
                  <rect x="3" y="12" width="12" height="2" rx="0.5" />
                </svg>
              </button>
            </div>
          </div>

          <div className="toolbar-divider" />

          {/* Layout options */}
          <div className="toolbar-group">
            <div className="toolbar-buttons">
              {LAYOUT_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  className={`toolbar-btn ${layout === opt.value ? 'active' : ''}`}
                  onClick={() => handleLayoutChange(opt.value)}
                  title={opt.label}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {layout !== 'single' && (
            <>
              <div className="toolbar-divider" />
              <button 
                className="toolbar-btn add-image-btn"
                onClick={triggerFileUpload}
                disabled={uploading}
              >
                + Add
              </button>
            </>
          )}
        </div>
      )}

      {/* Image content */}
      <div className="image-content" style={{ width: sizePercent }}>
        {layout === 'single' ? (
          hasMainImage ? (
            <div 
              className="image-wrapper has-aspect"
              style={{ aspectRatio }}
            >
              <img src={imageUrls['main']} alt={data.alt || data.caption || 'Document image'} />
              {isEditing && (
                <button className="remove-btn" onClick={handleRemoveMainImage} title="Remove image">×</button>
              )}
            </div>
          ) : (
            <div className="image-placeholder">
              {isEditing ? (
                <>
                  <div className="upload-label" onClick={triggerFileUpload}>
                    <span className="upload-icon">🖼</span>
                    <span>{uploading ? 'Uploading...' : 'Click to upload image'}</span>
                  </div>
                  {error && <div className="upload-error">{error}</div>}
                </>
              ) : (
                <span className="no-image">No image</span>
              )}
            </div>
          )
        ) : (
          <div className={`image-grid ${layout}`}>
            {hasMainImage && (
              <div 
                className="image-wrapper has-aspect"
                style={{ aspectRatio }}
              >
                <img src={imageUrls['main']} alt={data.alt || 'Document image'} />
                {isEditing && (
                  <button className="remove-btn" onClick={handleRemoveMainImage} title="Remove">×</button>
                )}
              </div>
            )}
            
            {images.map(img => (
              <div 
                key={img.id} 
                className="image-wrapper has-aspect"
                style={{ aspectRatio }}
              >
                {imageUrls[img.id] ? (
                  <>
                    <img src={imageUrls[img.id]} alt={img.alt || 'Document image'} />
                    {isEditing && (
                      <button className="remove-btn" onClick={() => handleRemoveImage(img.id)} title="Remove">×</button>
                    )}
                  </>
                ) : (
                  <div className="image-loading">Loading...</div>
                )}
              </div>
            ))}

            {!hasMainImage && images.length === 0 && isEditing && (
              <div className="image-placeholder" onClick={triggerFileUpload}>
                <span className="upload-icon">🖼</span>
                <span>{uploading ? 'Uploading...' : 'Click to add images'}</span>
              </div>
            )}
          </div>
        )}

        {/* Only occupy caption space when there is a caption, or while the block is
            focused for editing. An empty placeholder row would make the builder's image
            block taller than the PDF's and shift everything below it. */}
        {layout === 'single' && (data.caption || (isEditing && isFocused)) && (
          <LiveText
            className="image-caption"
            value={data.caption || ''}
            editable={isEditing}
            restoreKey={`${block.id}:caption`}
            onCommit={handleCaptionChange}
            data-placeholder="Add a caption..."
          />
        )}
      </div>

      {error && hasAnyImages && <div className="upload-error">{error}</div>}
    </div>
  );
};

export default ImageBlock;
